"use client";

import { c as _c } from "react/compiler-runtime";
import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { getInsuranceDecisionCoverage } from "@/configuration/groups/tpr/workflows/insurance-decision-coverage";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { toast } from "@/src/hooks/use-toast";
import { useConfirmInsuranceNetworkMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { receivingAssignmentOutputSchema } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
export function TprNetworkStatus(t0) {
  const $ = _c(50);
  const {
    task,
    steps,
    coverage,
    requirements
  } = t0;
  const mutation = useConfirmInsuranceNetworkMutation();
  const invalidate = useInvalidateServerComponent("getReferralInstructions");
  let t1;
  if ($[0] !== steps) {
    const assignmentStep = steps.find(_temp);
    t1 = receivingAssignmentOutputSchema.safeParse(assignmentStep?.output);
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsed = t1;
  const assignment = parsed.success ? parsed.data : null;
  let t2;
  if ($[2] !== coverage) {
    t2 = getInsuranceDecisionCoverage(coverage);
    $[2] = coverage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const policy = t2;
  let t3;
  if ($[4] !== coverage.coverages || $[5] !== policy.insuranceId || $[6] !== policy.insurancePackageId) {
    let t4;
    if ($[8] !== policy.insuranceId || $[9] !== policy.insurancePackageId) {
      t4 = recorded => recorded.insuranceId === policy.insuranceId && recorded.insurancePackageId === policy.insurancePackageId;
      $[8] = policy.insuranceId;
      $[9] = policy.insurancePackageId;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = coverage.coverages.find(t4);
    $[4] = coverage.coverages;
    $[5] = policy.insuranceId;
    $[6] = policy.insurancePackageId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const snapshot = t3;
  const policyLabel = `${snapshot?.casePolicy ? "Case policy" : "Insurance"}: ${snapshot?.planName || "Athena package"} (package ${policy.insurancePackageId})`;
  if (policy.selfPay) {
    return null;
  }
  if (requirements.clinicNetwork === "in_network" && requirements.providerNetwork === "in_network") {
    let t4;
    if ($[11] !== policyLabel) {
      t4 = <p className="font-medium">{policyLabel}</p>;
      $[11] = policyLabel;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const t5 = requirements.networkVerification ? "In-network \u2014 confirmed by Ops" : "In-network \u2014 confirmed by insurance grid";
    let t6;
    if ($[13] !== t5) {
      t6 = <p className="font-medium">{t5}</p>;
      $[13] = t5;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <p className="text-muted-foreground">Both the clinic and receiving provider accept this coverage. No additional network confirmation is needed. Coverage eligibility and authorization are checked separately.</p>;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t4 || $[17] !== t6) {
      t8 = <div className="space-y-1 rounded-md border bg-muted/50 p-3 text-sm">{t4}{t6}{t7}</div>;
      $[16] = t4;
      $[17] = t6;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    return t8;
  }
  if (!task.patient?.id || !assignment?.providerNpi || !assignment.targetDepartmentId || assignment.scope === "manual_review" || task.status === TaskStatus.PROCESSING) {
    return null;
  }
  const variantKey = requirements.networkVariantKey;
  if (variantKey === null || variantKey === undefined) {
    let t4;
    if ($[19] !== policyLabel) {
      t4 = <p className="font-medium">{policyLabel}</p>;
      $[19] = policyLabel;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    let t5;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p>Identify the insurance plan variant before confirming in-network status.</p>;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    let t6;
    if ($[22] !== t4) {
      t6 = <div className="space-y-1 text-sm">{t4}{t5}</div>;
      $[22] = t4;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  let t4;
  if ($[24] !== assignment.providerNpi || $[25] !== assignment.targetDepartmentId || $[26] !== policy.insuranceId || $[27] !== policy.insurancePackageId || $[28] !== task.patient.id || $[29] !== variantKey) {
    t4 = {
      expectedVariantKey: variantKey,
      patientId: task.patient.id,
      insuranceId: policy.insuranceId,
      packageNumber: policy.insurancePackageId,
      providerNpi: assignment.providerNpi,
      departmentId: assignment.targetDepartmentId
    };
    $[24] = assignment.providerNpi;
    $[25] = assignment.targetDepartmentId;
    $[26] = policy.insuranceId;
    $[27] = policy.insurancePackageId;
    $[28] = task.patient.id;
    $[29] = variantKey;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  const scope = t4;
  let t5;
  if ($[31] !== invalidate || $[32] !== mutation || $[33] !== scope || $[34] !== task.displayId || $[35] !== task.id) {
    t5 = async () => {
      ;
      try {
        await mutation.mutateAsync({
          taskId: task.id,
          displayId: task.displayId,
          scope
        });
        refreshTprWorkflowProgress(invalidate);
        toast({
          title: "In-network status confirmed"
        });
      } catch (t6) {
        const error = t6;
        toast({
          title: "Unable to save network confirmation",
          description: error instanceof Error ? error.message : "Refresh and try again.",
          variant: "destructive"
        });
      }
    };
    $[31] = invalidate;
    $[32] = mutation;
    $[33] = scope;
    $[34] = task.displayId;
    $[35] = task.id;
    $[36] = t5;
  } else {
    t5 = $[36];
  }
  const confirm = t5;
  let t6;
  if ($[37] !== policyLabel) {
    t6 = <p className="text-sm font-medium">{policyLabel}</p>;
    $[37] = policyLabel;
    $[38] = t6;
  } else {
    t6 = $[38];
  }
  let t7;
  if ($[39] !== assignment.providerFamilyName || $[40] !== assignment.providerGivenName) {
    t7 = <p className="text-sm">After verifying that the clinic and {assignment.providerGivenName} {assignment.providerFamilyName} accept this patient's selected coverage, confirm below. This also applies to future documents and referrals for this patient while the insurance policy, provider, and clinic stay the same.</p>;
    $[39] = assignment.providerFamilyName;
    $[40] = assignment.providerGivenName;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  const t8 = mutation.isPending ? "Saving..." : "Confirm in-network";
  let t9;
  if ($[42] !== confirm || $[43] !== mutation.isPending || $[44] !== t8) {
    t9 = <Button size="sm" onClick={confirm} disabled={mutation.isPending}>{t8}</Button>;
    $[42] = confirm;
    $[43] = mutation.isPending;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== t6 || $[47] !== t7 || $[48] !== t9) {
    t10 = <div className="space-y-2">{t6}{t7}{t9}</div>;
    $[46] = t6;
    $[47] = t7;
    $[48] = t9;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  return t10;
}
function _temp(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT && step.status === TaskStepStatus.COMPLETED;
}